import type { NewsEntryFragment, StaffMemberThumbFragment } from '#graphql-operations'

/**
 * Registers a journal entry as an `Article` schema.org node, with its author
 * (a staff member) embedded as a `Person` and the site's clinic identity as
 * `publisher`.
 */
export function useArticleSchema(data: MaybeRefOrGetter<NewsEntryFragment | undefined>) {
  const siteConfig = useSiteConfig()

  const nodes = computed(() => {
    const entry = toValue(data)
    if (!entry)
      return []

    const author = entry.newsAuthor?.[0] as StaffMemberThumbFragment | undefined
    const thumbImage = entry.thumbMedia?.[0]

    return [{
      '@type': 'Article',
      'headline': entry.title,
      'description': entry.descriptionText || entry.excerptText,
      'url': entry.url,
      'datePublished': entry.datePublishedIso,
      'dateModified': entry.dateUpdatedIso,
      'image': thumbImage
        ? {
            '@type': 'ImageObject',
            'url': thumbImage.url,
            'width': thumbImage.width,
            'height': thumbImage.height,
          }
        : undefined,
      'author': author
        ? {
            '@type': 'Person',
            'name': author.title,
            'jobTitle': author.role,
            'url': author.url,
            'image': author.thumbMedia?.[0]?.url,
          }
        : undefined,
      'publisher': {
        '@type': 'MedicalBusiness',
        'name': siteConfig.name,
        'url': siteConfig.url,
      },
    }]
  })

  useSchemaOrg(nodes)
}
